import { useCallback, useEffect, useState } from 'react';
import { BookOpen, Ban, Loader2, Search, Save, ChevronLeft, ChevronRight, Plus, X } from 'lucide-react';
import { apiFetch } from './apiBase';
import ReconExclusions from './ReconExclusions';
import MappingSnapshot from './MappingSnapshot';

const DEFAULT_SOURCE = 'payroll-recon';
const CELL_INPUT = 'input-field-sm min-w-[5rem] font-mono';
const CELL_TEXTAREA = 'input-field-sm min-w-[12rem] font-mono';
const CELL_SELECT = 'select-field-sm min-w-[4rem]';
const FORM_LABEL = 'font-medium text-slate-300';

export default function MappingCatalog() {
  const [studioView, setStudioView] = useState('mappings');
  const [sources, setSources] = useState([]);
  const [source, setSource] = useState(DEFAULT_SOURCE);
  const [catalog, setCatalog] = useState(null);
  const [rows, setRows] = useState([]);
  const [editable, setEditable] = useState([]);
  const [label, setLabel] = useState('');
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    apiFetch('/api/catalog').then(setCatalog).catch(() => {});
    apiFetch('/api/mappings/sources')
      .then((d) => setSources(d.sources || []))
      .catch((e) => setError(e.message));
  }, []);

  const loadMappings = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page: String(page), limit: '75', q });
      const data = await apiFetch(`/api/mappings/${source}?${params}`);
      setRows(data.rows || []);
      setEditable(data.editable || []);
      setLabel(data.label || source);
      setTotal(data.total || 0);
      setTotalPages(data.totalPages || 1);
      setDirty({});
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [source, page, q]);

  useEffect(() => {
    loadMappings();
  }, [loadMappings]);

  const onSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQ(searchInput.trim());
  };

  const setCell = (rowId, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r))
    );
    setDirty((d) => ({
      ...d,
      [rowId]: { ...(d[rowId] || {}), [field]: value },
    }));
  };

  const resetNewRow = () => {
    setNewRow(emptyNewRow(source));
    setShowAddForm(false);
  };

  const addRow = async (e) => {
    e.preventDefault();
    setAdding(true);
    setError('');
    setSaveMsg('');
    try {
      const { row } = await apiFetch(`/api/mappings/${source}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRow),
      });
      resetNewRow();
      setPage(1);
      const searchTerm =
        row.classificationName ||
        row.oracleElementName ||
        row.adpWageTypeCode ||
        row.id ||
        '';
      setQ(searchTerm);
      setSearchInput(searchTerm);
      setSaveMsg(`Added mapping ${row.id || row.classificationName || ''}`);
      setTimeout(() => setSaveMsg(''), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setAdding(false);
    }
  };

  useEffect(() => {
    setNewRow(emptyNewRow(source));
    setShowAddForm(false);
  }, [source]);

  const saveRow = async (rowId) => {
    const patch = dirty[rowId];
    if (!patch || !Object.keys(patch).length) return;
    setSavingId(rowId);
    setSaveMsg('');
    try {
      await apiFetch(`/api/mappings/${source}/${encodeURIComponent(rowId)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      setDirty((d) => {
        const next = { ...d };
        delete next[rowId];
        return next;
      });
      setSaveMsg(`Saved ${rowId}`);
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingId(null);
    }
  };

  const displayColumns = buildColumns(source, editable, rows[0]);

  const ogpi = catalog?.ogpi || {};
  const wt = catalog?.wageTypeSourceList || {};

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-cyan-600" />
          <div>
            <h2 className="text-xl font-semibold text-slate-100">Mapping Studio</h2>
            <p className="text-sm text-slate-500">{label}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs text-slate-500">
          <span>OGPI fields: {ogpi.fieldMappingCount ?? '—'}</span>
          <span>·</span>
          <span>WT rows: {wt.rowCount ?? '—'}</span>
          <span>·</span>
          <span>ICP: {catalog?.icpPlanMappings ?? '—'}</span>
          <span>·</span>
          <span>{total} rows in view</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-700 pb-3">
        <button
          type="button"
          onClick={() => setStudioView('mappings')}
          className={
            studioView === 'mappings' ? 'tab-pill-active' : 'tab-pill-inactive'
          }
        >
          <BookOpen className="h-4 w-4" />
          Payroll mappings
        </button>
        <button
          type="button"
          onClick={() => setStudioView('exclusions')}
          className={
            studioView === 'exclusions'
              ? 'tab-pill-active !bg-amber-700 !text-white'
              : 'tab-pill-inactive'
          }
        >
          <Ban className="h-4 w-4" />
          Recon exclusions
        </button>
      </div>

      {studioView === 'exclusions' && <ReconExclusions />}

      {studioView === 'mappings' && (
        <>
      <div className="flex flex-wrap gap-2">
        {sources.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => {
              setSource(s.id);
              setPage(1);
              setQ('');
              setSearchInput('');
            }}
            className={source === s.id ? 'tab-pill-active text-xs' : 'tab-pill-inactive text-xs'}
          >
            {s.label}
          </button>
        ))}
      </div>

      <MappingSnapshot source={source} label={label} />

      <form onSubmit={onSearch} className="flex flex-wrap gap-2">
        <div className="relative min-w-[240px] flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search wage type, Oracle element, classification…"
            className="input-field py-2 pl-9 pr-3"
          />
        </div>
        <button type="submit" className="btn-secondary">
          Search
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => {
            setShowAddForm((v) => !v);
            if (!showAddForm) setNewRow(emptyNewRow(source));
          }}
        >
          {showAddForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showAddForm ? 'Cancel' : 'Add mapping'}
        </button>
      </form>

      {showAddForm && (
        <form onSubmit={addRow} className="card-panel space-y-3 p-4">
          <h3 className="text-sm font-semibold text-slate-100">New mapping row</h3>
          <p className="text-xs text-slate-500">
            Fill in the fields below and click <strong>Create row</strong>. The row is added to{' '}
            {label}.
          </p>
          <AddMappingFields source={source} value={newRow} onChange={setNewRow} />
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={adding} className="btn-primary">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Create row
            </button>
            <button type="button" className="btn-secondary" onClick={resetNewRow}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {saveMsg && <p className="text-sm text-emerald-400">{saveMsg}</p>}
      {error && <p className="alert-error text-sm">{error}</p>}

      <p className="text-xs text-slate-500">
        Use <strong>Add mapping</strong> for new rows, or edit cells inline and click Save. Set{' '}
        <strong>Include in recon</strong> to <strong>No</strong> to skip a mapping pair. For whole elements or
        wage types, use <strong>Recon exclusions</strong> above. Changes are saved automatically.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading mappings…
        </div>
      ) : (
        <div className="card-panel overflow-x-auto">
          <table className="mapping-table data-table w-full min-w-[720px] text-left text-xs">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-800/80 text-slate-400">
                {displayColumns.map((col) => (
                  <th key={col.key} className="px-2 py-2 font-medium">
                    {col.label}
                  </th>
                ))}
                <th className="px-2 py-2 w-20">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={displayColumns.length + 1} className="px-2 py-8 text-center text-slate-500">
                    No mappings match your search.
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const rowDirty = dirty[row.id];
                  const isDirty = rowDirty && Object.keys(rowDirty).length > 0;
                  return (
                    <tr
                      key={row.id}
                      className={isDirty ? 'row-dirty' : ''}
                    >
                      {displayColumns.map((col) => (
                        <td key={col.key} className="px-2 py-1.5 align-top">
                          <MappingCell
                            row={row}
                            field={col.key}
                            editable={editable.includes(col.key)}
                            onChange={(v) => setCell(row.id, col.key, v)}
                          />
                        </td>
                      ))}
                      <td className="px-2 py-1.5">
                        <button
                          type="button"
                          disabled={!isDirty || savingId === row.id}
                          onClick={() => saveRow(row.id)}
                          className="btn-secondary gap-1 px-2 py-1 text-xs disabled:opacity-40"
                          title="Save row"
                        >
                          {savingId === row.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Save className="h-3 w-3" />
                          )}
                          Save
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex items-center justify-between text-sm text-slate-400">
        <span>
          Page {page} of {totalPages} ({total} total)
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="btn-secondary py-1"
          >
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="btn-secondary py-1"
          >
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
        </>
      )}
    </div>
  );
}

function buildColumns(source, editable, sample) {
  if (source === 'classification') {
    return [
      { key: 'classificationName', label: 'Classification' },
      { key: 'description', label: 'Description' },
      { key: 'registerWageTypeCodes', label: 'Wage type codes' },
      { key: 'registerLongTextInclude', label: 'Long text include' },
      { key: 'registerLongTextExclude', label: 'Long text exclude' },
    ];
  }
  if (source === 'ogpi-elements') {
    return [
      { key: 'infotype', label: 'IT' },
      { key: 'oracleElementName', label: 'Oracle element' },
      { key: 'adpWageTypeCode', label: 'ADP code' },
      { key: 'adpWageTypeName', label: 'ADP name' },
      { key: 'frequency', label: 'Frequency' },
    ];
  }
  if (source === 'icp-plan-elements') {
    return [
      { key: 'plan', label: 'ICP plan' },
      { key: 'optionName', label: 'Option' },
      { key: 'oracleElementName', label: 'Oracle / ICP name' },
      { key: 'adpWageTypeCode', label: 'ADP code' },
      { key: 'adpWageTypeName', label: 'ADP wage type' },
      { key: 'adpRecurrence', label: 'Recurrence' },
      { key: 'reconInclude', label: 'Include in recon' },
    ];
  }
  const cols = [
    { key: 'id', label: 'ID' },
    { key: 'adpWageTypeCode', label: 'ADP code' },
    { key: 'adpWageTypeLongText', label: 'ADP long text' },
    { key: 'oracleElementName', label: 'Oracle element' },
    { key: 'reconInclude', label: 'Include in recon' },
  ];
  if (sample?.category) cols.splice(4, 0, { key: 'category', label: 'Category' });
  return cols.filter((c) => c.key === 'id' || editable.includes(c.key) || sample?.[c.key] !== undefined);
}

function emptyNewRow(source) {
  if (source === 'classification') {
    return {
      classificationName: '',
      description: '',
      registerWageTypeCodes: '',
      registerLongTextInclude: '',
      registerLongTextExclude: '',
    };
  }
  if (source === 'ogpi-elements') {
    return {
      infotype: '0014',
      oracleElementName: '',
      adpWageTypeCode: '',
      adpWageTypeName: '',
      frequency: '',
    };
  }
  if (source === 'icp-plan-elements') {
    return {
      plan: '',
      optionName: '',
      oracleElementName: '',
      adpWageTypeCode: '',
      adpWageTypeName: '',
      adpRecurrence: '',
      reconInclude: false,
    };
  }
  return {
    adpWageTypeCode: '',
    adpWageTypeLongText: '',
    oracleElementName: '',
    reconInclude: true,
  };
}

function AddMappingFields({ source, value, onChange }) {
  const set = (field, v) => onChange((prev) => ({ ...prev, [field]: v }));

  if (source === 'classification') {
    return (
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm sm:col-span-2">
          <span className={FORM_LABEL}>Classification name *</span>
          <input
            required
            value={value.classificationName || ''}
            onChange={(e) => set('classificationName', e.target.value)}
            className="input-field mt-1"
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className={FORM_LABEL}>Description</span>
          <input
            value={value.description || ''}
            onChange={(e) => set('description', e.target.value)}
            className="input-field mt-1"
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className={FORM_LABEL}>Wage type codes (comma-separated)</span>
          <textarea
            value={value.registerWageTypeCodes || ''}
            onChange={(e) => set('registerWageTypeCodes', e.target.value)}
            rows={2}
            className="input-field mt-1 font-mono text-xs"
          />
        </label>
        <label className="text-sm">
          <span className={FORM_LABEL}>Long text include</span>
          <textarea
            value={value.registerLongTextInclude || ''}
            onChange={(e) => set('registerLongTextInclude', e.target.value)}
            rows={2}
            className="input-field mt-1 font-mono text-xs"
          />
        </label>
        <label className="text-sm">
          <span className={FORM_LABEL}>Long text exclude</span>
          <textarea
            value={value.registerLongTextExclude || ''}
            onChange={(e) => set('registerLongTextExclude', e.target.value)}
            rows={2}
            className="input-field mt-1 font-mono text-xs"
          />
        </label>
      </div>
    );
  }

  if (source === 'icp-plan-elements') {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-sm">
          <span className={FORM_LABEL}>ICP plan</span>
          <input
            value={value.plan || ''}
            onChange={(e) => set('plan', e.target.value)}
            placeholder="e.g. Relocation"
            className="input-field mt-1"
          />
        </label>
        <label className="text-sm">
          <span className={FORM_LABEL}>Option name *</span>
          <input
            value={value.optionName || ''}
            onChange={(e) => set('optionName', e.target.value)}
            className="input-field mt-1"
          />
        </label>
        <label className="text-sm sm:col-span-2 lg:col-span-1">
          <span className={FORM_LABEL}>Oracle / element name</span>
          <input
            value={value.oracleElementName || ''}
            onChange={(e) => set('oracleElementName', e.target.value)}
            placeholder="Defaults to option name"
            className="input-field mt-1"
          />
        </label>
        <label className="text-sm">
          <span className={FORM_LABEL}>ADP code *</span>
          <input
            value={value.adpWageTypeCode || ''}
            onChange={(e) => set('adpWageTypeCode', e.target.value)}
            className="input-field mt-1 font-mono"
          />
        </label>
        <label className="text-sm sm:col-span-2">
          <span className={FORM_LABEL}>ADP wage type</span>
          <input
            value={value.adpWageTypeName || ''}
            onChange={(e) => set('adpWageTypeName', e.target.value)}
            className="input-field mt-1"
          />
        </label>
        <label className="text-sm">
          <span className={FORM_LABEL}>Include in recon</span>
          <select
            value={value.reconInclude ? 'true' : 'false'}
            onChange={(e) => set('reconInclude', e.target.value === 'true')}
            className="input-field mt-1"
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </label>
      </div>
    );
  }

  if (source === 'ogpi-elements') {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="text-sm">
          <span className={FORM_LABEL}>Infotype</span>
          <select
            value={value.infotype || '0014'}
            onChange={(e) => set('infotype', e.target.value)}
            className="input-field mt-1"
          >
            <option value="0014">0014</option>
            <option value="0015">0015</option>
          </select>
        </label>
        <label className="text-sm">
          <span className={FORM_LABEL}>Oracle element *</span>
          <input
            value={value.oracleElementName || ''}
            onChange={(e) => set('oracleElementName', e.target.value)}
            className="input-field mt-1"
          />
        </label>
        <label className="text-sm">
          <span className={FORM_LABEL}>ADP code</span>
          <input
            value={value.adpWageTypeCode || ''}
            onChange={(e) => set('adpWageTypeCode', e.target.value)}
            className="input-field mt-1 font-mono"
          />
        </label>
        <label className="text-sm">
          <span className={FORM_LABEL}>ADP name</span>
          <input
            value={value.adpWageTypeName || ''}
            onChange={(e) => set('adpWageTypeName', e.target.value)}
            className="input-field mt-1"
          />
        </label>
        <label className="text-sm">
          <span className={FORM_LABEL}>Frequency</span>
          <input
            value={value.frequency || ''}
            onChange={(e) => set('frequency', e.target.value)}
            placeholder="Bi-Weekly"
            className="input-field mt-1"
          />
        </label>
        <p className="text-xs text-slate-500 sm:col-span-2 lg:col-span-3">
          At least one of Oracle element or ADP code is required.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      <label className="text-sm">
        <span className={FORM_LABEL}>ADP wage type code</span>
        <input
          value={value.adpWageTypeCode || ''}
          onChange={(e) => set('adpWageTypeCode', e.target.value)}
          placeholder="710E"
          className="input-field mt-1 font-mono"
        />
      </label>
      <label className="text-sm sm:col-span-2">
        <span className={FORM_LABEL}>ADP long text</span>
        <input
          value={value.adpWageTypeLongText || ''}
          onChange={(e) => set('adpWageTypeLongText', e.target.value)}
          className="input-field mt-1"
        />
      </label>
      <label className="text-sm sm:col-span-2 lg:col-span-3">
        <span className={FORM_LABEL}>Oracle element name</span>
        <input
          value={value.oracleElementName || ''}
          onChange={(e) => set('oracleElementName', e.target.value)}
          placeholder="Employee Medical Contribution"
          className="input-field mt-1"
        />
      </label>
      <label className="text-sm">
        <span className={FORM_LABEL}>Include in recon</span>
        <select
          value={value.reconInclude ? 'true' : 'false'}
          onChange={(e) => set('reconInclude', e.target.value === 'true')}
          className="input-field mt-1"
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </label>
      <p className="text-xs text-slate-500 sm:col-span-2 lg:col-span-3">
        At least one of ADP code or Oracle element is required. ID is auto-generated as{' '}
        <code className="text-xs">WT-{'{code}'}</code> when a code is provided.
      </p>
    </div>
  );
}

function MappingCell({ row, field, editable, onChange }) {
  const value = row[field];
  if (!editable) {
    const display =
      typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value == null ? '—' : String(value);
    return <span className="cell-readonly">{display}</span>;
  }
  if (field === 'reconInclude') {
    return (
      <select
        value={value ? 'true' : 'false'}
        onChange={(e) => onChange(e.target.value === 'true')}
        className={CELL_SELECT}
      >
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  const isLong =
    field === 'registerWageTypeCodes' ||
    field === 'registerLongTextInclude' ||
    field === 'registerLongTextExclude' ||
    field === 'adpWageTypeLongText';
  if (isLong) {
    return (
      <textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        rows={2}
        className={CELL_TEXTAREA}
      />
    );
  }
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      className={CELL_INPUT}
    />
  );
}
